import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { cn } from '@/utils/cn'
import { copyToClipboard } from '@/utils/copyToClipboard'
import { getChainNameById } from '@/utils/network/getChainNameById'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Address, Chain } from 'viem'
import { mainnet } from 'viem/chains'
import { useChains, useDisconnect, useEnsName, useSwitchChain } from 'wagmi'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../common/accordion'
import { Button } from '../common/button'
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../common/dropdownMenu'
import { NetworkImage } from '../common/networkImage'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { Tooltip, TooltipContent } from '../common/tooltip'
import { ArrowIcon } from '../icons/arrow'
import { CheckIcon } from '../icons/check'
import { CopyIcon } from '../icons/copy'
import { LogOutIcon } from '../icons/logOut'

interface Props {
  className?: string
  account: Address
  modal?: boolean
}

export const ConnectionMenu = ({ className, account, modal }: Props) => {
  const { chain } = useAccountContext()
  const { data: ensName } = useEnsName({ address: account, chainId: mainnet.id })
  const renderedAddress = ensName ?? shortenAddress(account)
  const { trackEvent } = useMixpanel()
  const [copiedSuccessfully, setCopiedSuccessfully] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const handleCopy = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      trackEvent('Address copied in connection menu')
      try {
        await copyToClipboard(account)
        setCopiedSuccessfully(true)
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setCopiedSuccessfully(false), SECOND_IN_MILLISECONDS * 2)
      } catch (error) {
        console.error('Failed to copy address to clipboard', error)
      }
    },
    [account, trackEvent]
  )

  return (
    <DropdownMenu modal={modal}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="input"
          size="md"
          className={cn('group gap-2 px-3', className)}
          onClick={() => trackEvent('Header connection menu clicked')}
        >
          <div className="flex items-center gap-2">
            <img
              src={`https://effigy.im/a/${account}.svg`}
              alt={renderedAddress}
              className="size-5 min-w-5 rounded-full"
            />
            {renderedAddress}
          </div>
          <div className="group-data-[state=open]:-scale-y-100 transition-transform">
            <ArrowIcon direction="down" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center justify-between gap-2 p-4">
          <div className="flex items-center gap-2">
            <img
              src={`https://effigy.im/a/${account}.svg`}
              alt={renderedAddress}
              className="size-10 min-w-10 rounded-full"
            />
            {renderedAddress}
          </div>
          <Tooltip onClick={handleCopy} className="rounded-full">
            <div>
              {copiedSuccessfully ? (
                <CheckIcon size={20} className="fade-in animate-in text-success" />
              ) : (
                <CopyIcon size={20} className="fade-in animate-in" />
              )}
            </div>
            <TooltipContent>
              <p>Copy address</p>
            </TooltipContent>
          </Tooltip>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="bg-surface">
          <NetworkSwitch connectedChain={chain} />
          <DropdownMenuSeparator />
          <DisconnectButton />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const NetworkSwitch = ({ connectedChain }: { connectedChain: Chain | undefined }) => {
  const { mutate: switchChain } = useSwitchChain()
  const allChains = useChains()

  const chains = useMemo(() => {
    return [...allChains].sort((a, b) => {
      return getNetworkName(a.name.toLowerCase()).localeCompare(getNetworkName(b.name.toLowerCase()))
    })
  }, [allChains])

  const handleChainSwitch = (event: MouseEvent, chain: Chain) => {
    event.preventDefault()
    switchChain({ chainId: chain.id })
  }

  return (
    <DropdownMenuItem onSelect={(e) => e?.preventDefault()}>
      <Accordion type="multiple" className="w-full">
        <AccordionItem value="network">
          <AccordionTrigger asChild>
            <DropdownMenuButton className="group flex h-10 items-center justify-between gap-2">
              {connectedChain ? (
                <div className="flex items-center gap-2">
                  <NetworkImage network={getChainNameById(connectedChain.id)} />
                  <span className="capitalize">{getNetworkName(getChainNameById(connectedChain.id))}</span>
                </div>
              ) : (
                <span className="text-destructive">Wrong network</span>
              )}
              <div className="group-data-[state=open]:-scale-y-100 transition-transform">
                <ArrowIcon direction="down" />
              </div>
            </DropdownMenuButton>
          </AccordionTrigger>
          <AccordionContent>
            <ScrollArea className="flex max-h-36 flex-col overflow-y-auto">
              <ul className="flex flex-col border-t bg-page">
                {chains.map((chain: Chain) => {
                  const chainName = getNetworkName(getChainNameById(chain.id))

                  return (
                    <li key={chain.id} className="group">
                      <DropdownMenuItem>
                        <DropdownMenuButton
                          size="sm"
                          className="justify-between"
                          onClick={(event: MouseEvent) => handleChainSwitch(event, chain)}
                        >
                          <div className="flex items-center gap-2">
                            <NetworkImage network={getChainNameById(chain.id)} />
                            <span className="capitalize">{chainName}</span>
                          </div>
                          {connectedChain?.id === chain.id && (
                            <CheckIcon size={16} className="text-success transition-colors" />
                          )}
                        </DropdownMenuButton>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="group-last:hidden" />
                    </li>
                  )
                })}
              </ul>
              <ScrollBar />
            </ScrollArea>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </DropdownMenuItem>
  )
}

const DisconnectButton = () => {
  const { mutate: disconnect } = useDisconnect()

  return (
    <DropdownMenuItem>
      <DropdownMenuButton className="hocus:text-destructive text-destructive" onClick={() => disconnect()}>
        <LogOutIcon size={24} />
        <span>Disconnect wallet</span>
      </DropdownMenuButton>
    </DropdownMenuItem>
  )
}
